import React, { useState, useEffect } from "react";
import { DataTable, Pagination, Button } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";
import { Eye } from "lucide-react";

export const DamagedItemsTable = ({ damagedItems, onView }) => {
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;
    
    // Ensure we have an array
    const data = Array.isArray(damagedItems) ? damagedItems : [];

    useEffect(() => {
        setPage(1);
    }, [damagedItems]);

    const columns = [
        {
            header: "Invoice",
            render: (r) => (
                <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">
                    {r.returnRef?.invoiceNo || r.returnRef?.originalInvoiceNo || r.invoice || "N/A"}
                </span>
            )
        },
        {
            header: "Date",
            render: (r) => (
                <span className="text-[12px] text-slate-500 font-medium">
                    {r.date ? new Date(r.date).toLocaleDateString() : "N/A"}
                </span>
            )
        },
        {
            header: "Type",
            render: (r) => (
                <span className="text-[12px] font-medium text-slate-600 uppercase tracking-widest">
                    {r.type || "UNKNOWN"}
                </span>
            )
        },
        {
            header: "Damaged Items",
            render: (r) => (
                <div className="flex flex-col gap-1">
                    {(r.damagedItems || []).slice(0, 2).map((item, idx) => (
                        <div key={idx} className="flex flex-col">
                            <span className="font-semibold text-slate-800 text-[12px] truncate max-w-[180px]">
                                {Number(item.damagedQty) || 0}x {item.item?.name || item.itemName || "Unknown Item"}
                            </span>
                            {item.reason && (
                                <span className="text-[10px] text-slate-400 font-normal uppercase tracking-tight truncate max-w-[180px]">
                                    {item.reason}
                                </span>
                            )}
                        </div>
                    ))}
                    {(r.damagedItems || []).length > 2 && (
                        <span className="text-[10px] text-slate-400 font-normal uppercase tracking-tight">
                            +{(r.damagedItems || []).length - 2} more items
                        </span>
                    )}
                    {(r.damagedItems || []).length === 0 && (
                        <span className="text-[12px] font-medium text-slate-600 italic">
                            No items
                        </span>
                    )}
                </div>
            )
        },
        {
            header: "Total Damaged Qty",
            render: (r) => (
                <span className="text-[12px] font-medium text-slate-800">
                    {r.totalDamagedQty || 0}
                </span>
            )
        },
        {
            header: "Damaged Value",
            render: (r) => {
                // Fallback for legacy items that didn't record rate
                let value = r.totalDamagedValue || 0;
                if (value === 0 && r.damagedItems) {
                    value = r.damagedItems.reduce((acc, curr) => acc + ((curr.damagedQty || 0) * (curr.rate || 0)), 0);
                    // If rate is still 0 (due to old backend), it remains 0, but if frontend can supply it, it will.
                }
                return (
                    <span className="text-[12px] font-medium text-[#f97316]">
                        {fmt(value)}
                    </span>
                );
            }
        },
        {
            header: "Action",
            render: (r) => (
                <div className="flex gap-1.5 items-center">
                    <Button variant="ghost" size="icon-xs" onClick={() => onView(r)} title="View Details">
                        <Eye size={14} />
                    </Button>
                </div>
            )
        }
    ];

    const currentData = data.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const totalPages = Math.max(Math.ceil(data.length / itemsPerPage), 1);

    return (
        <div className="card rounded-none overflow-hidden border-slate-100 shadow-sm">
            <DataTable columns={columns} data={currentData} />
            
            {data.length > itemsPerPage && (
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                />
            )}
        </div>
    );
};

